import { useFlag } from '@reflag/react-sdk';
import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';
import { useSetRecoilState } from 'recoil';

import type * as api from '@gitbook/api-client';
import type {
    NavigationAccountSettingsTab,
    NavigationOrgSettingsTab,
    NavigationScreenAccountSettings,
    NavigationScreenOrgSettings,
} from '@gitbook/app-navigation';
import { canPlanMigrateToNewPricing } from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import {
    Sidebar,
    SidebarBody,
    SidebarEmptyState,
    SidebarHeader,
    SidebarItemList,
    SidebarLink,
    SidebarSection,
    SidebarSectionHeading,
} from '@gitbook/spine-react';

import { LayoutSidebarContext } from '../../AppLayout';
import { getLastSeenOrgId } from '../../navigation';
import { apiQuery, desktopSidebarCollapsedAtom } from '../../state';

/**
 * Sidebar for settings pages.
 */
export function SettingsSidebar(props: {
    screen: NavigationScreenAccountSettings | NavigationScreenOrgSettings;
}) {
    const { screen } = props;

    const {
        data: { items: userOrganizations },
    } = useSuspenseQuery(apiQuery('listOrganizationsForAuthenticatedUser', { limit: 100 }));

    useAutoOpenSidebar();
    const organization = useSettingsOrganization({ userOrganizations, screen });

    return (
        <Sidebar>
            <SidebarHeader>
                <SidebarLink
                    label="Back to app"
                    data-testid="settings.closeButton"
                    icon={Icon.ArrowLeft}
                    href={{
                        overlay: null,
                        screen: {
                            type: 'root',
                            organization: organization?.id || undefined,
                        },
                    }}
                />
            </SidebarHeader>

            <SidebarBody>
                <AccountSection screen={screen} />
                {organization && (
                    <OrganizationSection screen={screen} organization={organization} />
                )}
            </SidebarBody>
        </Sidebar>
    );
}

/**
 * Get the organization ID to use in the settings screen.
 */
function useSettingsOrganization(props: {
    userOrganizations: api.Organization[];
    screen: NavigationScreenAccountSettings | NavigationScreenOrgSettings;
}): api.Organization | null {
    const { userOrganizations, screen } = props;
    return React.useMemo(() => {
        // Get the first accessible organization
        // If the user has no organizations, we return null
        const firstOrg = userOrganizations[0];
        if (!firstOrg) {
            return null;
        }

        // Organization is defined in the screen
        const orgIdFromScreen = 'organization' in screen ? screen.organization : null;
        if (orgIdFromScreen) {
            const orgFromScreen = userOrganizations.find((org) => org.id === orgIdFromScreen);
            if (orgFromScreen) {
                return orgFromScreen;
            }
        }

        // Organization is the last seen organization
        const lastSeenOrgId = getLastSeenOrgId();
        if (lastSeenOrgId) {
            const lastSeenOrg = userOrganizations.find((org) => org.id === lastSeenOrgId);
            if (lastSeenOrg) {
                return lastSeenOrg;
            }
        }

        // Organization is the first accessible organization
        return firstOrg;
    }, [userOrganizations, screen]);
}

/**
 * Automatically open the sidebar.
 */
function useAutoOpenSidebar() {
    const { isCollapsed } = React.useContext(LayoutSidebarContext);
    const setSidebarCollapsed = useSetRecoilState(desktopSidebarCollapsedAtom);
    React.useEffect(() => {
        if (isCollapsed) {
            // we always want the settings sidebar to be open on desktop
            // so we force it open if it's not
            setSidebarCollapsed(false);
        }
    }, [isCollapsed, setSidebarCollapsed]);
}

function AccountSection(props: {
    screen: NavigationScreenAccountSettings | NavigationScreenOrgSettings;
}) {
    const { screen } = props;

    const navigationPropsToTab = (tab: NavigationAccountSettingsTab) => {
        return {
            href: {
                current: {
                    type: 'account',
                    tab,
                },
            } as const,
            isActive: screen.type === 'account' && screen.tab === tab,
        };
    };

    return (
        <SidebarSection>
            <SidebarSectionHeading>Account</SidebarSectionHeading>
            <SidebarItemList>
                <SidebarLink
                    label="General"
                    icon={Icon.Settings}
                    {...navigationPropsToTab('profile')}
                />
                <SidebarLink
                    label="Notifications"
                    icon={Icon.Bell}
                    {...navigationPropsToTab('notification')}
                />
                <SidebarLink
                    label="Organizations"
                    icon={Icon.Organization}
                    {...navigationPropsToTab('organizations')}
                />
                <SidebarLink
                    label="Developer settings"
                    icon={Icon.Code}
                    {...navigationPropsToTab('developer')}
                />
            </SidebarItemList>
        </SidebarSection>
    );
}

function OrganizationSection(props: {
    screen: NavigationScreenAccountSettings | NavigationScreenOrgSettings;
    organization: api.Organization;
}) {
    const { screen, organization } = props;

    const featureDocsAgents = useFlag('DOCS_AGENTS');

    const navigationPropsToTab = (tab: NavigationOrgSettingsTab) => {
        return {
            href: {
                current: {
                    type: 'org_settings',
                    organization: organization.id,
                    tab,
                },
            } as const,
            isActive:
                screen.type === 'org_settings' &&
                screen.organization === organization.id &&
                screen.tab.type === tab.type,
        };
    };

    return (
        <SidebarSection>
            <SidebarSectionHeading>Organization</SidebarSectionHeading>
            {organization.permissions.admin ? (
                <SidebarItemList>
                    <SidebarLink
                        label="General"
                        icon={Icon.Organization}
                        {...navigationPropsToTab({ type: 'profile' })}
                    />
                    <SidebarLink
                        label="Members"
                        icon={Icon.Users}
                        {...navigationPropsToTab({ type: 'members' })}
                    />
                    <SidebarLink
                        label="Merge rules"
                        icon={Icon.MergeRulesAllow}
                        {...navigationPropsToTab({ type: 'rules' })}
                    />
                    {featureDocsAgents.isEnabled ? (
                        <SidebarLink
                            label="GitBook Agent"
                            icon={Icon.AIAgent}
                            {...navigationPropsToTab({ type: 'agents' })}
                        />
                    ) : null}
                    <SidebarLink
                        label="Invite links"
                        icon={Icon.Link}
                        {...navigationPropsToTab({ type: 'invites' })}
                    />
                    {organization.permissions.ownTeam && (
                        <SidebarLink
                            label="Teams"
                            icon={Icon.Team}
                            {...navigationPropsToTab({ type: 'teams' })}
                        />
                    )}
                    <SidebarLink
                        label="SSO"
                        icon={Icon.Sso}
                        {...navigationPropsToTab({ type: 'sso' })}
                    />
                    <SidebarLink
                        label="Billing"
                        icon={Icon.Upgrade}
                        {...navigationPropsToTab({ type: 'plans' })}
                    />
                    {canPlanMigrateToNewPricing(organization.plan) && (
                        <SidebarLink
                            label="Upgrade plan"
                            icon={Icon.Upgrade}
                            {...navigationPropsToTab({ type: 'upgrade' })}
                        />
                    )}
                </SidebarItemList>
            ) : (
                <SidebarEmptyState>
                    You don’t have admin access to that organization.
                </SidebarEmptyState>
            )}
        </SidebarSection>
    );
}
